import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Camera, Sparkles, Loader2, DollarSign, Calendar, Tag, Receipt, Plus, AlertTriangle, Image as ImageIcon, Pencil, RotateCcw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import TokenPurchaseModal from '../TokenPurchaseModal';
import SelectBookingOrProjectModal from '../manage/SelectBookingOrProjectModal';
import { useAppSystem } from '../../hooks/useAppSystem';
import {
  createProcessingExpenseRecord,
  runReceiptBackgroundJob,
  type BackgroundReceiptJobInput,
} from '../../lib/expenseReceiptBackgroundProcessor';
import {
  computeFileSha256,
  DuplicateReceiptError,
  findDuplicateReceipt,
  formatDuplicateReceiptMessage,
  type DuplicateReceiptMatch,
} from '../../lib/expenseReceiptDuplicateCheck';

interface ExpenseCategory {
  id: string;
  name: string;
  color?: string;
}

interface UploadReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  businessId: string;
  categories: ExpenseCategory[];
  onCategoriesChange: () => void;
  initialBookingId?: string;
  initialProjectId?: string;
  /** Pre-loaded file (e.g. from email attachment) to skip the upload step */
  initialFile?: File | null;
  initialPreviewUrl?: string | null;
  /**
   * When provided AND on mobile, replaces the dropzone with a live camera view.
   * The "Manual" button calls this and forwards any booking/project the user has already pre-selected so the
   * manual modal can pick up from where the camera flow left off.
   */
  onSwitchToManual?: (
    preselection?: { bookingId?: string | null; projectId?: string | null }
  ) => void;
}

export default function UploadReceiptModal({
  isOpen,
  onClose,
  onSave,
  businessId,
  categories,
  onCategoriesChange,
  initialBookingId,
  initialProjectId,
  initialFile,
  initialPreviewUrl,
  onSwitchToManual
}: UploadReceiptModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [duplicateMatch, setDuplicateMatch] = useState<DuplicateReceiptMatch | null>(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [pendingFileHash, setPendingFileHash] = useState<string | null>(null);
  const [showTokenPurchase, setShowTokenPurchase] = useState(false);
  const [isSelectBookingOrProjectModalOpen, setIsSelectBookingOrProjectModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<{ id: string; title: string; booking_number: number | null } | null>(null);
  const [selectedProject, setSelectedProject] = useState<{ id: string; title: string } | null>(null);
  
  // App system hooks
  const { coreApps, installedAddons } = useAppSystem();
  const hasCalendarApp = coreApps.some(app => app.app?.slug === 'calendar') || 
                        installedAddons.some(app => app.app?.slug === 'calendar');
  const hasProjectsApp = coreApps.some(app => app.app?.slug === 'projects') || 
                        installedAddons.some(app => app.app?.slug === 'projects');
  
  // Form fields
  const [bookingId, setBookingId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [categoryId, setCategoryId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [taxBreakdown, setTaxBreakdown] = useState<{ name: string; rate: string; amount: string }[]>([]);
  const [notes, setNotes] = useState('');
  const [isTaxDeductible, setIsTaxDeductible] = useState(true);

  // Inline add-category
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isSavingCategory, setIsSavingCategory] = useState(false);

  // AI extracted data
  const [aiData, setAiData] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [isAnimating, setIsAnimating] = useState(false);

  // Responsive mobile detection with state
  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' && window.innerWidth < 640
  );

  // Live camera viewfinder (mobile only)
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const liveStreamRef = useRef<MediaStream | null>(null);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [isShutterFlashing, setIsShutterFlashing] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check on mount

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /** Show the live camera ONLY on mobile + no preview yet + no pre-loaded file + a Manual escape route is provided. */
  const shouldShowLiveCamera =
    isMobile && !previewUrl && !initialFile && !!onSwitchToManual && isOpen;

  const stopLiveCamera = useCallback(() => {
    const stream = liveStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      liveStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startLiveCamera = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not available in this browser');
      return;
    }
    setIsCameraStarting(true);
    setCameraError(null);
    try {
      stopLiveCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      liveStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch {
          // Some browsers reject play() before user gesture; the stream is still active
          // and the video will auto-play once the user interacts.
        }
      }
    } catch (err) {
      console.error('Camera error:', err);
      const message =
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Camera permission denied. Enable camera access in Settings to scan receipts.'
          : 'Could not access the camera on this device.';
      setCameraError(message);
    } finally {
      setIsCameraStarting(false);
    }
  }, [facingMode, stopLiveCamera]);

  // Start camera when we enter the live-camera state; stop on exit/unmount
  useEffect(() => {
    if (shouldShowLiveCamera) {
      void startLiveCamera();
    } else {
      stopLiveCamera();
    }
    return () => {
      stopLiveCamera();
    };
  }, [shouldShowLiveCamera, startLiveCamera, stopLiveCamera]);

  const handleFlipCamera = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  /** Capture the current viewfinder frame as a JPEG File and feed it into the normal pipeline. */
  const handleSnapPhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      toast.error('Camera is not ready yet');
      return;
    }
    setIsShutterFlashing(true);
    setTimeout(() => setIsShutterFlashing(false), 180);

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error('Could not capture photo');
          return;
        }
        const file = new File([blob], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelectedFile(file);
        setAiData(null);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
        void runDuplicateCheck(file);
      },
      'image/jpeg',
      0.92,
    );
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setSelectedFile(null);
      setPreviewUrl(null);
      setAiData(null);
      setDuplicateMatch(null);
      setPendingFileHash(null);
      
      // Pre-select booking or project if provided
      if (initialBookingId) {
        setBookingId(initialBookingId);
        setProjectId('');
        setSelectedProject(null);
        fetchBookingDetails(initialBookingId);
      } else if (initialProjectId) {
        setProjectId(initialProjectId);
        setBookingId('');
        setSelectedBooking(null);
        fetchProjectDetails(initialProjectId);
      } else {
        setBookingId('');
        setProjectId('');
        setSelectedBooking(null);
        setSelectedProject(null);
      }
      
      setAmount('');
      setDescription('');
      setMerchantName('');
      setExpenseDate(new Date().toISOString().split('T')[0]);
      setCategoryId('');
      setPaymentMethod('');
      setTaxBreakdown([]);
      setNotes('');
    }
  }, [isOpen, initialBookingId, initialProjectId]);

  const runDuplicateCheck = async (file: File) => {
    setIsCheckingDuplicate(true);
    setDuplicateMatch(null);
    setPendingFileHash(null);
    try {
      const hash = await computeFileSha256(file);
      setPendingFileHash(hash);
      const match = await findDuplicateReceipt(businessId, hash);
      setDuplicateMatch(match);
    } catch (err) {
      console.error('Duplicate check failed:', err);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  // When an initial file arrives (async from wrapper fetch), populate preview only
  useEffect(() => {
    if (!isOpen || !initialFile) return;
    setSelectedFile(initialFile);
    setPreviewUrl(initialPreviewUrl || initialFile.name);
    void runDuplicateCheck(initialFile);
  }, [initialFile, initialPreviewUrl, isOpen, businessId]);

  const fetchBookingDetails = async (bookingId: string) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('id, title, booking_number')
        .eq('id', bookingId)
        .single();

      if (!error && data) {
        setSelectedBooking({
          id: data.id,
          title: data.title || 'Untitled Booking',
          booking_number: data.booking_number
        });
      }
    } catch (error) {
      console.error('Error fetching booking details:', error);
    }
  };

  const fetchProjectDetails = async (projectId: string) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, title')
        .eq('id', projectId)
        .single();

      if (!error && data) {
        setSelectedProject({
          id: data.id,
          title: data.title || 'Untitled Project'
        });
      }
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const handleAddCategory = async () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    if (categories.some(c => c.name.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('Category already exists');
      return;
    }
    setIsSavingCategory(true);
    try {
      const maxOrder = categories.reduce((max, c: any) => Math.max(max, c.order_index || 0), -1);
      const { data, error } = await supabase
        .from('expense_categories')
        .insert({ business_id: businessId, name: trimmed, color: '#3B82F6', order_index: maxOrder + 1, is_system: false, is_active: true })
        .select()
        .single();
      if (error) throw error;
      toast.success(`Category "${trimmed}" added`);
      setCategoryId(data.id);
      setNewCategoryName('');
      setIsAddingCategory(false);
      onCategoriesChange();
    } catch (error: any) {
      console.error('Error adding category:', error);
      toast.error(error.message || 'Failed to add category');
    } finally {
      setIsSavingCategory(false);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isAccepted = file.type.startsWith('image/') || file.type === 'application/pdf';
    if (!isAccepted) {
      toast.error('Please select an image or PDF file');
      return;
    }

    setSelectedFile(file);
    setAiData(null);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(file.name);
    }
    void runDuplicateCheck(file);
  };

  /** Handle photo from native camera (PWA/iOS) via file input with capture - keeps modal open. */
  const handleCameraFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
    setAiData(null);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
    void runDuplicateCheck(file);
    e.target.value = ''; // reset so same device can take another photo
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error('Please upload a receipt or file');
      return;
    }

    if (duplicateMatch) {
      toast.error(formatDuplicateReceiptMessage(duplicateMatch));
      return;
    }

    const expenseId = crypto.randomUUID();
    let fileHash = pendingFileHash;
    if (!fileHash) {
      try {
        fileHash = await computeFileSha256(selectedFile);
        const match = await findDuplicateReceipt(businessId, fileHash);
        if (match) {
          setDuplicateMatch(match);
          toast.error(formatDuplicateReceiptMessage(match));
          return;
        }
      } catch (err) {
        if (err instanceof DuplicateReceiptError) {
          setDuplicateMatch(err.match);
          toast.error(err.message);
          return;
        }
        throw err;
      }
    }

    const jobInput: BackgroundReceiptJobInput = {
      expenseId,
      businessId,
      file: selectedFile,
      fileHash: fileHash ?? undefined,
      categories,
      bookingId: bookingId || null,
      projectId: projectId || null,
      categoryId: categoryId || null,
      amount: amount ? parseFloat(amount) : null,
      description: description?.trim() || null,
      merchantName: merchantName?.trim() || null,
      expenseDate: expenseDate || new Date().toISOString().split('T')[0],
      paymentMethod: paymentMethod || null,
      taxBreakdown,
      notes: notes?.trim() || null,
      isTaxDeductible,
      manualAiData: aiData,
    };

    setIsProcessing(true);
    try {
      await createProcessingExpenseRecord(jobInput);
      toast.success('Receipt saved — AI is processing in the background');
      onSave();
      void runReceiptBackgroundJob(jobInput);
    } catch (error: unknown) {
      console.error('Error queueing expense:', error);
      if (error instanceof DuplicateReceiptError) {
        setDuplicateMatch(error.match);
        toast.error(error.message);
        return;
      }
      const message =
        error instanceof Error ? error.message : 'Failed to save expense';
      toast.error(message);
    } finally {
      setIsProcessing(false);
    }
  };

  // iOS/mobile: full-screen live camera viewfinder. Tapping shutter -> falls through to existing form preview.
  const liveCameraContent = (
    <div
      className="fixed inset-0 bg-black"
      style={{
        zIndex: 100065,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
      onClick={e => e.stopPropagation()}
    >
      {/* Hidden file input for photo library picker */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Live video stream */}
      {cameraError ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-white">
          <Camera className="h-12 w-12 mb-4 text-white/70" />
          <p className="text-base font-semibold mb-1">Camera unavailable</p>
          <p className="text-sm text-white/70 mb-6 max-w-xs">{cameraError}</p>
          <div className="flex flex-col gap-2 w-full max-w-xs">
            <button
              type="button"
              onClick={() => void startLiveCamera()}
              className="px-4 py-3 bg-white text-black rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-3 bg-white/15 text-white rounded-xl font-medium text-sm active:scale-[0.98] transition-transform"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Choose photo or file
            </button>
            {onSwitchToManual && (
              <button
                type="button"
                onClick={() =>
                  onSwitchToManual({
                    bookingId: bookingId || null,
                    projectId: projectId || null,
                  })
                }
                className="px-4 py-3 bg-white/15 text-white rounded-xl font-medium text-sm active:scale-[0.98] transition-transform"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Enter manually
              </button>
            )}
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          playsInline
          muted
        />
      )}

      {/* Off-screen canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Shutter flash overlay */}
      <div
        className="pointer-events-none absolute inset-0 bg-white transition-opacity duration-150"
        style={{ opacity: isShutterFlashing ? 0.9 : 0 }}
      />

      {/* Top bar */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))',
        }}
      >
        <button
          type="button"
          onClick={() => {
            stopLiveCamera();
            onClose();
          }}
          className="h-10 w-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-[0.95] transition-transform"
          aria-label="Close"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2 text-white">
          <Receipt className="h-4 w-4 opacity-80" />
          <span className="text-sm font-semibold tracking-tight">Scan Receipt</span>
        </div>

        <button
          type="button"
          onClick={handleFlipCamera}
          disabled={isCameraStarting || !!cameraError}
          className="h-10 w-10 flex items-center justify-center rounded-full bg-black/40 text-white active:scale-[0.95] transition-transform disabled:opacity-40"
          aria-label="Flip camera"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <RotateCcw className="h-5 w-5" />
        </button>
      </div>

      {/* Framing hint */}
      {!cameraError && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div
            className="rounded-3xl border-2 border-white/70"
            style={{
              width: 'min(78vw, 360px)',
              height: 'min(58vh, 480px)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.18)',
            }}
          />
        </div>
      )}
      {!cameraError && (
        <div
          className="absolute left-0 right-0 flex justify-center pointer-events-none"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 64px)' }}
        >
          <span className="px-3 py-1 rounded-full bg-black/45 text-white text-[12px] font-medium tracking-tight">
            {isCameraStarting ? 'Starting camera…' : 'Position the receipt inside the frame'}
          </span>
        </div>
      )}

      {/* Bottom controls: assign pill (when enabled) + photo library / shutter / manual */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
          background: 'linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))',
        }}
      >
        {/* Assign-expense pill — lets users pre-attach a booking/project before snapping */}
        {(hasCalendarApp || hasProjectsApp) && (
          <div className="px-4 pt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setIsSelectBookingOrProjectModalOpen(true)}
              className="max-w-[88%] flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white active:scale-[0.97] transition-transform"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {selectedBooking || selectedProject ? (
                <>
                  <Tag className="h-4 w-4 shrink-0 text-white/90" />
                  <span className="text-[13px] font-semibold tracking-tight truncate max-w-[200px]">
                    {selectedBooking
                      ? `${selectedBooking.title}${selectedBooking.booking_number ? ` #${selectedBooking.booking_number}` : ''}`
                      : selectedProject!.title}
                  </span>
                  <span
                    role="button"
                    aria-label="Clear assignment"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBooking(null);
                      setSelectedProject(null);
                      setBookingId('');
                      setProjectId('');
                    }}
                    className="ml-1 h-6 w-6 -mr-1 flex items-center justify-center rounded-full bg-black/35 active:scale-[0.92] transition-transform"
                  >
                    <X className="h-3.5 w-3.5" />
                  </span>
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 shrink-0 text-white/90" />
                  <span className="text-[13px] font-semibold tracking-tight">
                    {hasCalendarApp && hasProjectsApp
                      ? 'Assign to booking or project'
                      : hasCalendarApp
                        ? 'Assign to booking'
                        : 'Assign to project'}
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        <div className={`flex items-center justify-between px-8 ${hasCalendarApp || hasProjectsApp ? 'pt-5' : 'pt-10'}`}>
          {/* Photo library */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="h-14 w-14 flex items-center justify-center rounded-2xl bg-white/15 text-white active:scale-[0.94] transition-transform"
            aria-label="Choose photo or file"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <ImageIcon className="h-7 w-7" />
          </button>

          {/* Shutter */}
          <button
            type="button"
            onClick={handleSnapPhoto}
            disabled={!!cameraError || isCameraStarting}
            className="relative h-20 w-20 rounded-full flex items-center justify-center active:scale-[0.96] transition-transform disabled:opacity-50"
            aria-label="Take photo"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span className="absolute inset-0 rounded-full border-[3px] border-white" />
            <span className="h-[60px] w-[60px] rounded-full bg-white" />
          </button>

          {/* Manual */}
          <button
            type="button"
            onClick={() => {
              stopLiveCamera();
              onSwitchToManual?.({
                bookingId: bookingId || null,
                projectId: projectId || null,
              });
            }}
            className="h-14 min-w-[56px] px-3 flex flex-col items-center justify-center rounded-2xl bg-white/15 text-white active:scale-[0.94] transition-transform"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Pencil className="h-5 w-5" />
            <span className="text-[11px] font-semibold mt-0.5 tracking-tight">Manual</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Structure matches CreateBookingModal: no padding on overlay; panel positioned with safe-area top/bottom
  const panelContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100065,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden relative">
        {/* Header */}
        <div className={`bg-gray-50 border-b border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} flex items-center justify-between sticky top-0 z-10`}>
          <h2 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>Upload Receipt</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className={`${isMobile ? 'h-5 w-5' : 'h-5 w-5'}`} />
          </button>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto overflow-x-hidden min-w-0 ${isMobile ? 'px-4 py-4' : 'px-6 py-6'} space-y-${isMobile ? '4' : '6'}`}>
          {/* Receipt Upload Section */}
          <div className="space-y-4">
            {!previewUrl ? (
              <div className={`border-2 border-dashed border-gray-200 rounded-2xl ${isMobile ? 'p-6' : 'p-12'} text-center bg-gray-50/50`}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleCameraFileSelect}
                  className="hidden"
                />
                <div className={isMobile ? 'mb-4' : 'mb-6'}>
                  <div className={`${isMobile ? 'w-16 h-16 mb-3' : 'w-20 h-20 mb-4'} mx-auto rounded-full bg-blue-50 flex items-center justify-center`}>
                    <Upload className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} text-blue-500`} />
                  </div>
                  <p className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-900 mb-1`}>Upload Receipt</p>
                  <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500`}>Take a photo or choose from library</p>
                </div>
                <div className={`flex ${isMobile ? 'flex-col gap-2' : 'gap-3'} justify-center`}>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`${isMobile ? 'w-full px-4 py-3.5 text-sm' : 'px-6 py-3 text-sm'} bg-white border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 transition-all shadow-sm touch-manipulation`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Choose File
                  </button>
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className={`${isMobile ? 'w-full px-4 py-3.5 text-sm' : 'px-6 py-3 text-sm'} bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 active:bg-blue-700 transition-all shadow-sm flex items-center ${isMobile ? 'justify-center' : ''} gap-2 touch-manipulation`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Camera className="h-4 w-4" />
                    Camera
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative rounded-2xl overflow-hidden bg-gray-100 border border-gray-200">
                  {selectedFile?.type === 'application/pdf' ? (
                    <div className="flex flex-col items-center justify-center py-10 px-4 bg-gray-50">
                      <Receipt className="h-12 w-12 text-red-500 mb-3" />
                      <div className="text-sm font-semibold text-gray-900 text-center truncate max-w-full px-4">{selectedFile.name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        PDF · {selectedFile.size > 1024 * 1024 ? `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(selectedFile.size / 1024)} KB`}
                      </div>
                    </div>
                  ) : (
                    <img
                      src={previewUrl!}
                      alt="Receipt"
                      className="w-full h-auto"
                    />
                  )}
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      setPreviewUrl(null);
                      setAiData(null);
                      setDuplicateMatch(null);
                      setPendingFileHash(null);
                    }}
                    className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white rounded-full transition-all active:scale-95"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {previewUrl && (
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-blue-600 shrink-0" />
                      <p className="text-sm text-blue-800">
                        AI will categorize this receipt after you save — you can close this screen right away.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {duplicateMatch ? (
            <div className="mx-4 sm:mx-6 mt-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 mt-0.5" aria-hidden />
              <div className="min-w-0 text-sm text-red-900">
                <p className="font-semibold">Duplicate receipt</p>
                <p className="mt-0.5">{formatDuplicateReceiptMessage(duplicateMatch)}</p>
                <p className="mt-1 text-xs text-red-800">
                  Choose a different file or remove the current receipt to continue.
                </p>
              </div>
            </div>
          ) : isCheckingDuplicate ? (
            <div className="mx-4 sm:mx-6 mt-4 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking for duplicates…
            </div>
          ) : null}

          {/* Form Fields - iOS Style */}
          <div className="space-y-5">
            {/* Assign Expense Field - Only show if Calendar or Projects app is installed */}
            {(hasCalendarApp || hasProjectsApp) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Expense
                </label>
                <button
                  type="button"
                  onClick={() => setIsSelectBookingOrProjectModalOpen(true)}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-left text-gray-900 hover:bg-gray-100 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-base flex items-center justify-between"
                >
                  <span className={selectedBooking || selectedProject ? 'text-gray-900' : 'text-gray-400'}>
                    {selectedBooking 
                      ? `${selectedBooking.title}${selectedBooking.booking_number ? ` #${selectedBooking.booking_number}` : ''}`
                      : selectedProject
                      ? selectedProject.title
                      : (() => {
                          if (hasCalendarApp && hasProjectsApp) return 'Select a booking or project (optional)';
                          if (hasCalendarApp) return 'Select a booking (optional)';
                          if (hasProjectsApp) return 'Select a project (optional)';
                          return 'Select (optional)';
                        })()}
                  </span>
                  {(selectedBooking || selectedProject) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBooking(null);
                        setSelectedProject(null);
                        setBookingId('');
                        setProjectId('');
                      }}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-200"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </button>
              </div>
            )}

            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4 min-w-0`}>
              <div className="min-w-0">
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <DollarSign className={`absolute ${isMobile ? 'left-3' : 'left-4'} top-1/2 transform -translate-y-1/2 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} />
                  <input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={`w-full min-w-0 ${isMobile ? 'pl-10 pr-3 py-3 text-base' : 'pl-12 pr-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
              <div className="min-w-0">
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                  Date <span className="text-red-500">*</span>
                </label>
                <div className="relative min-w-0 overflow-hidden rounded-xl">
                  <Calendar className={`absolute ${isMobile ? 'left-3' : 'left-4'} top-1/2 transform -translate-y-1/2 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400 pointer-events-none`} />
                  <input
                    type="date"
                    value={expenseDate}
                    onChange={(e) => setExpenseDate(e.target.value)}
                    className={`w-full min-w-0 max-w-full box-border ${isMobile ? 'pl-10 pr-3 py-3 text-base' : 'pl-12 pr-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                    style={{ minWidth: 0 }}
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                placeholder="What was this expense for?"
                required
              />
            </div>

            <div>
              <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                Merchant/Vendor
              </label>
              <input
                type="text"
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
                className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                placeholder="Store or vendor name"
              />
            </div>

            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                  Category
                </label>
                {isAddingCategory ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={e => setNewCategoryName(e.target.value)}
                      className={`flex-1 ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                      placeholder="Category name"
                      autoFocus
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleAddCategory();
                        if (e.key === 'Escape') { setIsAddingCategory(false); setNewCategoryName(''); }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      disabled={isSavingCategory || !newCategoryName.trim()}
                      className="px-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
                    >
                      {isSavingCategory ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsAddingCategory(false); setNewCategoryName(''); }}
                      className="px-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Tag className={`absolute ${isMobile ? 'left-3' : 'left-4'} top-1/2 transform -translate-y-1/2 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400 z-10`} />
                    <select
                      value={categoryId}
                      onChange={(e) => {
                        if (e.target.value === '__add_new__') {
                          setIsAddingCategory(true);
                        } else {
                          setCategoryId(e.target.value);
                        }
                      }}
                      className={`w-full ${isMobile ? 'pl-10 pr-3 py-3 text-base' : 'pl-12 pr-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none`}
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                      <option value="__add_new__">+ Add New Category</option>
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none`}
                >
                  <option value="">Select method</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="check">Check</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Tax Breakdown */}
            <div>
              <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                Tax
              </label>
              <div className="space-y-2">
                {taxBreakdown.map((tax, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={tax.name}
                      onChange={e => {
                        const updated = [...taxBreakdown];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        setTaxBreakdown(updated);
                      }}
                      className={`${isMobile ? 'w-24 px-2 py-2.5 text-sm' : 'w-28 px-3 py-3 text-sm'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                      placeholder="GST"
                    />
                    <div className="relative flex-1">
                      <DollarSign className={`absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400`} />
                      <input
                        type="number"
                        step="0.01"
                        value={tax.amount}
                        onChange={e => {
                          const updated = [...taxBreakdown];
                          updated[idx] = { ...updated[idx], amount: e.target.value };
                          setTaxBreakdown(updated);
                        }}
                        className={`w-full pl-8 pr-2 ${isMobile ? 'py-2.5 text-sm' : 'py-3 text-sm'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all`}
                        placeholder="0.00"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setTaxBreakdown(taxBreakdown.filter((_, i) => i !== idx))}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setTaxBreakdown([...taxBreakdown, { name: '', rate: '', amount: '' }])}
                  className={`flex items-center gap-1.5 ${isMobile ? 'text-xs px-3 py-2' : 'text-sm px-3 py-2'} text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors font-medium touch-manipulation`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Tax
                </button>
                {taxBreakdown.length > 0 && (
                  <div className={`flex justify-between items-center px-3 py-2 bg-gray-50 rounded-xl ${isMobile ? 'text-sm' : 'text-sm'}`}>
                    <span className="text-gray-500 font-medium">Total Tax</span>
                    <span className="font-semibold text-gray-900">
                      ${taxBreakdown.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0).toFixed(2)}
                    </span>
                  </div>
                )}
                {amount && (
                  <div className={`flex justify-between items-center px-3 py-2 bg-blue-50 rounded-xl ${isMobile ? 'text-sm' : 'text-sm'}`}>
                    <span className="text-blue-700 font-medium">Grand Total</span>
                    <span className="font-bold text-blue-900">
                      ${((parseFloat(amount) || 0) + taxBreakdown.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0)).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 ${isMobile ? 'mb-1.5' : 'mb-2'}`}>
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className={`w-full ${isMobile ? 'px-3 py-3 text-base' : 'px-4 py-3.5 text-base'} bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none`}
                placeholder="Additional notes..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 bg-white border-t border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} ${isMobile ? 'flex-col gap-2' : 'flex items-center justify-end gap-3'}`}>
          {isMobile && (
            <button
              onClick={handleSubmit}
              disabled={isProcessing || !selectedFile || !!duplicateMatch || isCheckingDuplicate}
              className={`w-full ${isMobile ? 'px-4 py-3.5 text-base' : 'px-4 py-2'} bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium touch-manipulation order-2`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Expense'
              )}
            </button>
          )}
          <button
            onClick={handleClose}
            className={`${isMobile ? 'w-full px-4 py-3 text-base order-1' : 'px-4 py-2'} text-gray-700 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            Cancel
          </button>
          {!isMobile && (
            <button
              onClick={handleSubmit}
              disabled={isProcessing || !selectedFile || !!duplicateMatch || isCheckingDuplicate}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Expense'
            )}
          </button>
          )}
        </div>
        </div>
      </div>

    </div>
  );

  // Nested modals that need to work in BOTH the camera view and the panel view
  const supportingModals = (
    <>
      {showTokenPurchase && (
        <TokenPurchaseModal
          isOpen={showTokenPurchase}
          onClose={() => setShowTokenPurchase(false)}
          businessId={businessId}
        />
      )}

      <SelectBookingOrProjectModal
        isOpen={isSelectBookingOrProjectModalOpen}
        onClose={() => setIsSelectBookingOrProjectModalOpen(false)}
        onSelectBooking={(booking) => {
          setSelectedBooking({
            id: booking.id,
            title: booking.title || 'Untitled Booking',
            booking_number: booking.booking_number,
          });
          setBookingId(booking.id);
          setSelectedProject(null);
          setProjectId('');
          setIsSelectBookingOrProjectModalOpen(false);
        }}
        onSelectProject={(project) => {
          setSelectedProject({
            id: project.id,
            title: project.title,
          });
          setProjectId(project.id);
          setSelectedBooking(null);
          setBookingId('');
          setIsSelectBookingOrProjectModalOpen(false);
        }}
        businessId={businessId}
        hasCalendarApp={hasCalendarApp}
        hasProjectsApp={hasProjectsApp}
      />
    </>
  );

  const modalContent = (
    <>
      {shouldShowLiveCamera ? liveCameraContent : panelContent}
      {supportingModals}
    </>
  );

  return createPortal(modalContent, document.body);
}

